import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, ReplaySubject, tap } from 'rxjs';
import { Navigation } from './navigation.types';
import { UserStateModel, UserStore } from '@hmax-order/frontend/state';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private _httpClient = inject(HttpClient);
  private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);
  private _userStore = inject(UserStore);
  readonly user$: Observable<UserStateModel> = toObservable(this._userStore.getUser);

  get user(): UserStateModel {
    return this._userStore.getUser();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for navigation
   */
  get navigation$(): Observable<Navigation> {
    return this._navigation.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all navigation data
   */
  get(): Observable<Navigation> {
    const user = this.user;
    const companyId = user?.currentCompanyId;
    const isSuperUser = user?.roles?.includes('SUPER') ?? false;
    const companiesCount = user?.companies?.length ?? 0;

    if (!companyId && isSuperUser) {
      return this.getSuperUserNavigation();
    }

    if (!companyId && companiesCount > 1) {
      return this.getOrganizationNavigation();
    }

    return this.getCompanyNavigation();
  }

  /**
   * Retornar navegação padrão de uma empresa/hotel
   */
  private getCompanyNavigation(): Observable<Navigation> {
    return this._httpClient.get<Navigation>('api/common/navigation').pipe(
      tap((navigation: any) => {
        this._navigation.next(navigation);
      }),
    );
  }

  /**
   * Aplica as regras de habilitação/desabilitação nos menus com base no binaries
   */
  private hasAnyRole(requiredRoles: string[]): boolean {
    return requiredRoles.some((role) => this.user.roles.includes(role));
  }

  private applySubscriptionRules(items: any[], binaries: string): any[] {
    const roleRules: Record<string, string[]> = {
      settings: ['SUPER', 'OWNER', 'MANAGER'],
      movement: ['SUPER', 'OWNER', 'MANAGER', 'CASHIER'],
      management: ['SUPER', 'OWNER', 'MANAGER', 'CASHIER'],
    };

    return items.map((item) => {
      const binaryDisabled = typeof item.binaryIndex === 'number' && binaries[item.binaryIndex] === '0';

      let disabled = binaryDisabled;

      if (item.binaryIndex === 3) {
        disabled = binaryDisabled || !this.hasAnyRole(['FINANCE', 'SUPER', 'OWNER']);
      }

      if (roleRules[item.id]) {
        disabled = !this.hasAnyRole(roleRules[item.id]);
      }

      return {
        ...item,
        disabled,
        ...(item.children?.length && {
          children: this.applySubscriptionRules(item.children, binaries),
        }),
      };
    });
  }

  /**
   * Retornar navegação de organização de empresas
   */
  getOrganizationNavigation(): Observable<Navigation> {
    return this._httpClient.get<Navigation>('api/common/organization-navigation').pipe(
      tap((navigation) => {
        this._navigation.next(navigation);
      }),
    );
  }

  /**
   * Retornar navegação de super usuário
   */
  getSuperUserNavigation(): Observable<Navigation> {
    return this._httpClient.get<Navigation>('api/common/super-navigation').pipe(
      tap((navigation) => {
        // const user = this._store.selectSnapshot<UserStateModel>((state) => state.user);
        // const isSuper = user?.roles?.includes('SUPER');
        // if (!isSuper) {
        //   throw new Error('Forbidden resource');
        // }
        this._navigation.next(navigation);
      }),
    );
  }

  /**
   * Retornar navegação de Demo Viewer
   */
  getDemoViewerNavigation(): Observable<Navigation> {
    return this._httpClient.get<Navigation>('api/common/demo-viewer-navigation').pipe(
      tap((navigation) => {
        this._navigation.next(navigation);
      }),
    );
  }
}
